import { formatLearningUiEnglish, type LearningUiTranslator } from '@features/learning/shared/learning-ui-copy'
import type { FlashcardCard } from '@services/firebase/flashcards'
import type {
  FlashcardGameSession,
  FlashcardPrompt,
  FlashcardPromptOption,
  FlashcardPromptPair,
} from '@features/flashcards/state/flashcards-state'
import { resolvePreferredAudioSource } from '@shared/media/audio/audio-source'
import { resolveFlashcardTranslation } from '@features/flashcards/shared/flashcard-language'

function shuffle<T>(items: readonly T[]) {
  return [...items].sort(() => Math.random() - 0.5)
}

function repeatableSample<T>(items: readonly T[], count: number) {
  if (items.length === 0) {
    return []
  }

  const pool = shuffle(items)
  const result: T[] = []

  while (result.length < count) {
    result.push(pool[result.length % pool.length])
  }

  return shuffle(result)
}

function normalizeAnswerText(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function sanitizeFlashcardGameText(value?: string | null) {
  return (value ?? '')
    .replace(/[\u200B-\u200D\u2060\uFEFF\uFFFD]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function getCardLabel(card: FlashcardCard) {
  return sanitizeFlashcardGameText(card.title) || 'Flashcard'
}

function getCardTranslation(card: FlashcardCard, locale: string) {
  return sanitizeFlashcardGameText(resolveFlashcardTranslation(card, locale)) || getCardLabel(card)
}

function getCardAudio(card: FlashcardCard) {
  return resolvePreferredAudioSource({ card, profile: null })
}

function createPromptId(template: FlashcardPrompt['template'], index: number) {
  return `flashcard-prompt-${template}-${index}-${Math.random().toString(36).slice(2, 8)}`
}

function createOptionId(cardId: string, suffix: string, index: number) {
  return `${cardId}-${suffix}-${index}`
}

function buildMultipleChoicePrompt({
  ui,
  cards,
  locale,
  optionCount,
  promptIndex,
}: {
  readonly ui: LearningUiTranslator
  readonly cards: FlashcardCard[]
  readonly locale: string
  readonly optionCount: number
  readonly promptIndex: number
}): FlashcardPrompt {
  const minimumOptionCount = Math.max(optionCount, 2)
  const textOptionCards = repeatableSample(cards, minimumOptionCount)
  const textCorrectIndex = Math.floor(Math.random() * textOptionCards.length)
  const textCorrectCard = textOptionCards[textCorrectIndex]
  const textCorrectLabel = getCardLabel(textCorrectCard)
  const textCorrectTranslation = getCardTranslation(textCorrectCard, locale)
  const promptAudio = getCardAudio(textCorrectCard)
  const audioOptionCards = repeatableSample(
    cards.filter((card) => Boolean(getCardAudio(card))),
    minimumOptionCount,
  )
  const audioCorrectIndex = audioOptionCards.length
    ? Math.floor(Math.random() * audioOptionCards.length)
    : -1
  const audioCorrectCard = audioOptionCards[audioCorrectIndex] ?? null
  const imageOptionCards = repeatableSample(
    cards.filter((card) => Boolean(card.featuredImage)),
    minimumOptionCount,
  )
  const imageCorrectIndex = imageOptionCards.length
    ? Math.floor(Math.random() * imageOptionCards.length)
    : -1
  const imageCorrectCard = imageOptionCards[imageCorrectIndex] ?? null
  const variants = [
    {
      answerText: textCorrectTranslation,
      answerTranslation: textCorrectTranslation,
      options: textOptionCards.map((card, index) => ({
        id: createOptionId(card.id, 'translation', index),
        isCorrect: index === textCorrectIndex,
        label: getCardTranslation(card, locale),
      })),
      question: ui("Choose the translation for \"{{label}}\".", { label: textCorrectLabel }),
      status: Boolean(textCorrectCard.title),
    },
    {
      answerText: textCorrectLabel,
      answerTranslation: textCorrectTranslation,
      options: textOptionCards.map((card, index) => ({
        id: createOptionId(card.id, 'title', index),
        isCorrect: index === textCorrectIndex,
        label: getCardLabel(card),
      })),
      question: ui("Choose the word that matches \"{{label}}\".", { label: textCorrectTranslation }),
      status: Boolean(textCorrectTranslation),
    },
    {
      answerText: textCorrectLabel,
      answerTranslation: textCorrectTranslation,
      audio: promptAudio,
      options: textOptionCards.map((card, index) => ({
        id: createOptionId(card.id, 'listen', index),
        isCorrect: index === textCorrectIndex,
        label: getCardLabel(card),
      })),
      question: ui("Listen and choose the correct flashcard."),
      status: Boolean(promptAudio),
    },
    {
      answerText: textCorrectLabel,
      answerTranslation: textCorrectTranslation,
      image: textCorrectCard.featuredImage,
      options: textOptionCards.map((card, index) => ({
        id: createOptionId(card.id, 'image', index),
        isCorrect: index === textCorrectIndex,
        label: getCardLabel(card),
      })),
      question: ui("Look at the image and choose the correct flashcard."),
      status: Boolean(textCorrectCard.featuredImage),
    },
    {
      answerText: audioCorrectCard ? getCardLabel(audioCorrectCard) : undefined,
      answerTranslation: audioCorrectCard ? getCardTranslation(audioCorrectCard, locale) : undefined,
      options: audioOptionCards.map((card, index) => ({
        audio: getCardAudio(card),
        id: createOptionId(card.id, 'audio-answer', index),
        isCorrect: index === audioCorrectIndex,
      })),
      question: audioCorrectCard
        ? ui("Choose the correct sound for \"{{label}}\".", { label: getCardLabel(audioCorrectCard) })
        : '',
      status: audioOptionCards.length >= minimumOptionCount && audioCorrectIndex >= 0,
    },
    {
      answerText: imageCorrectCard ? getCardLabel(imageCorrectCard) : undefined,
      answerTranslation: imageCorrectCard ? getCardTranslation(imageCorrectCard, locale) : undefined,
      options: imageOptionCards.map((card, index) => ({
        id: createOptionId(card.id, 'image-answer', index),
        image: card.featuredImage,
        isCorrect: index === imageCorrectIndex,
      })),
      question: imageCorrectCard
        ? ui("Choose the correct image for \"{{label}}\".", { label: getCardLabel(imageCorrectCard) })
        : '',
      status: imageOptionCards.length >= minimumOptionCount && imageCorrectIndex >= 0,
    },
  ].filter((variant) => variant.status)

  const variant = variants[Math.floor(Math.random() * variants.length)] ?? variants[0]

  return {
    answerText: variant.answerText,
    answerTranslation: variant.answerTranslation,
    audio: variant.audio ?? null,
    id: createPromptId(1, promptIndex),
    image: variant.image,
    options: variant.options as FlashcardPromptOption[],
    question: variant.question,
    template: 1,
  }
}

function buildMatchPrompt({
  ui,
  cards,
  locale,
  promptIndex,
}: {
  readonly ui: LearningUiTranslator
  readonly cards: FlashcardCard[]
  readonly locale: string
  readonly promptIndex: number
}): FlashcardPrompt {
  const matchCards = repeatableSample(
    cards.filter((card) => Boolean(resolveFlashcardTranslation(card, locale))),
    3,
  )
  const matchPairs: FlashcardPromptPair[] = matchCards.map((card) => ({
    id: card.id,
    leftLabel: getCardLabel(card),
    rightLabel: getCardTranslation(card, locale),
  }))

  return {
    id: createPromptId(2, promptIndex),
    matchPairs,
    question: ui("Match each word with its translation."),
    template: 2,
  }
}

function buildArrangePrompt({
  ui,
  cards,
  locale,
  promptIndex,
}: {
  readonly ui: LearningUiTranslator
  readonly cards: FlashcardCard[]
  readonly locale: string
  readonly promptIndex: number
}): FlashcardPrompt {
  const card = repeatableSample(cards, 1)[0]
  const answerText = getCardLabel(card)

  return {
    answerText,
    answerTranslation: getCardTranslation(card, locale),
    id: createPromptId(3, promptIndex),
    question: ui("Arrange the letters to rebuild the word."),
    shuffledLetters: shuffle(answerText.split('')),
    template: 3,
  }
}

function buildFillPrompt({
  ui,
  cards,
  locale,
  promptIndex,
  template,
}: {
  readonly ui: LearningUiTranslator
  readonly cards: FlashcardCard[]
  readonly locale: string
  readonly promptIndex: number
  readonly template: 4 | 5
}): FlashcardPrompt {
  const card = repeatableSample(cards, 1)[0]
  const preferredAudio = getCardAudio(card)
  const translation = getCardTranslation(card, locale)
  const variants = [
    {
      audio: preferredAudio,
      promptLabel: ui("Audio clue"),
      question:
        template === 5
          ? ui("Listen, then say or type the flashcard title.")
          : ui("Listen and type the flashcard title."),
      status: Boolean(preferredAudio),
    },
    {
      image: card.featuredImage,
      promptLabel: ui("Image clue"),
      question:
        template === 5
          ? ui("Look at the image, then say or type the flashcard title.")
          : ui("Look at the image and type the flashcard title."),
      status: Boolean(card.featuredImage),
    },
    {
      promptLabel: ui("Translation clue"),
      question:
        template === 5
          ? ui("Say or type the word for \"{{label}}\".", { label: translation })
          : ui("Type the word for \"{{label}}\".", { label: translation }),
      status: Boolean(translation),
    },
  ].filter((variant) => variant.status)

  const variant = variants[Math.floor(Math.random() * variants.length)] ?? variants[0]

  return {
    answerText: getCardLabel(card),
    answerTranslation: translation,
    audio: variant.audio ?? null,
    id: createPromptId(template, promptIndex),
    image: variant.image,
    promptLabel: variant.promptLabel,
    question: variant.question,
    template,
  }
}

export function compareFlashcardAnswer({
  actual,
  expected,
}: {
  readonly actual: string
  readonly expected: string
}) {
  return normalizeAnswerText(actual) === normalizeAnswerText(expected)
}

export function createFlashcardGameSession({
  ui = formatLearningUiEnglish,
  cards,
  hasActivationKey,
  level,
  locale,
  supportsSpeech,
}: {
  readonly ui?: LearningUiTranslator
  readonly cards: FlashcardCard[]
  readonly hasActivationKey: boolean
  readonly level: 'basic' | 'advanced' | 'pro'
  readonly locale: string
  readonly supportsSpeech: boolean
}): FlashcardGameSession {
  if (cards.length === 0) {
    throw new Error(ui("No flashcards are available for this game session."))
  }

  if (level !== 'basic' && !hasActivationKey) {
    throw new Error(ui("A premium activation is required for this difficulty."))
  }

  const config = {
    advanced: { count: 15, templates: [1, 2, 3] as Array<1 | 2 | 3> },
    basic: { count: 10, templates: [1] as Array<1> },
    pro: {
      count: 20,
      templates: supportsSpeech
        ? ([1, 2, 3, 4, 5] as Array<1 | 2 | 3 | 4 | 5>)
        : ([1, 2, 3, 4] as Array<1 | 2 | 3 | 4>),
    },
  }[level]

  const prompts: FlashcardPrompt[] = Array.from({ length: config.count }, (_, index) => {
    const template = config.templates[Math.floor(Math.random() * config.templates.length)]

    switch (template) {
      case 2:
        return buildMatchPrompt({ ui, cards, locale, promptIndex: index })
      case 3:
        return buildArrangePrompt({ ui, cards, locale, promptIndex: index })
      case 4:
        return buildFillPrompt({ ui, cards, locale, promptIndex: index, template: 4 })
      case 5:
        return buildFillPrompt({ ui, cards, locale, promptIndex: index, template: 5 })
      default:
        return buildMultipleChoicePrompt({
          ui,
          cards,
          locale,
          optionCount: level === 'basic' ? 3 : 4,
          promptIndex: index,
        })
    }
  })

  return {
    activeIndex: 0,
    correctCount: 0,
    incorrectCount: 0,
    level,
    prompts,
    status: 'playing',
  }
}
